import React, {useMemo, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {useSearchParams} from "next/navigation";
import {useAppSelector} from "@/redux/hooks";
import {RootState} from "@/redux/store";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Skeleton,
    Stack,
    Typography
} from "@mui/material";

import FaIcon from "@/components/utilities/fa-icon";
import LayoutDialog from "@/components/layout/layout-dialog";
import ProductFilters from "./products-filters";
import useSearchLogic from "@/hooks/useSearchLogic";
import {defaultProductParams, ProductSortOrder} from "@/lib/products";
import {ProductsFiltersDialogProps} from "@/types/product";
import SelectedItemsFilters from "./selected-items-filters";
import {useProductsFilters} from "@/hooks/useProductsFilters";


export default function ProductsFiltersDialog({
                                                  open,
                                                  handleClose,
                                                  handleOpen,
                                                  productsData,
                                                  isLoading,
                                              }: ProductsFiltersDialogProps) {
    const theme = useTheme();
    const searchParams = useSearchParams();

    const [expandedAccordion, setExpandedAccordion] = useState(true);
    const [expandedAccordionSort, setExpandedAccordionSort] = useState(false);
    const sortOrder = Number(
        useAppSelector((state: RootState) => state.products.SortOrder)
    );
    const {totalSelectedItemsCount} = useSearchLogic();
    const {updateFilters} = useProductsFilters();

    const query = useMemo(() => {
        return Object.fromEntries(new URLSearchParams(searchParams.toString()).entries());
    }, [searchParams]);

    const handleChangeAccordion = (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded);
    };

    const handleChangeSortAccordion = (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordionSort(isExpanded);
    };

    const renderSortOption = (
        icon: string,
        label: string,
        value: ProductSortOrder
    ) => (
        <ListItem
            disablePadding
            onClick = {() => updateFilters({SortOrder: value})}
            secondaryAction = {
                sortOrder === value && (
                    <FaIcon
                        color = {theme.palette.primary.main}
                        variant = "solid"
                        faIcon = "fa-check"
                        sx = {{mr: 2, fontSize: 20}}
                    />
                )
            }
        >
            <ListItemButton >
                <FaIcon faIcon = {icon} sx = {{fontSize: 20, mx: 1}} />
                <ListItemText primary = {label} />
            </ListItemButton >
        </ListItem >
    );

    const isResultsReady = !isLoading && !!productsData;

    return (
        <>
            <Button
                id="products-filter-button"
                sx = {{fontSize: 14, minWidth: 5, p: 1}}
                startIcon = {
                    <Badge
                        variant = "dot"
                        invisible = {totalSelectedItemsCount < 1}
                        anchorOrigin = {{vertical: "top", horizontal: "left"}}
                        sx = {{
                            "& .MuiBadge-badge": {
                                minWidth: 8,
                                height: 8,
                                backgroundColor: "primary.light"
                            }
                        }}
                    >
                        <FaIcon fontSize = {16} faIcon = "fa-filter" />
                    </Badge >
                }
                onClick = {handleOpen}
            >
                فیلترها
            </Button >

            <LayoutDialog
                open = {open}
                handleClose = {handleClose}
                title = {"فیلترها" + (totalSelectedItemsCount > 0 ? ` (${totalSelectedItemsCount})` : "")}
                headerLeftContent = {
                    totalSelectedItemsCount > 0 && (
                        <Typography
                            fontSize = {14}
                            fontWeight = {400}
                            color = {theme.palette.primary.main}
                            pt = {3}
                            pl = {1}
                            sx = {{cursor: "pointer", position: "absolute", top: "0%", width: 500}}
                            onClick = {() => {
                                updateFilters({
                                    ...defaultProductParams,
                                    isExists: query.isExists === "true",
                                });
                            }}
                        >
                            حذف فیلترها
                        </Typography >
                    )
                }
                outOfContent = {
                    open && (
                        <SelectedItemsFilters >
                            <Box
                                display = "flex"
                                alignItems = "center"
                                justifyContent = "space-between"
                                width = "100%"
                            >
                                <Stack direction = "row" spacing = {0.5} alignItems = "center" >
                                    {isResultsReady ? (
                                        <>
                                            <Typography fontWeight = "bold" >
                                                {productsData?.totalCount}
                                            </Typography >
                                            <Typography >کالا پیدا شد.</Typography >
                                        </>
                                    ) : (
                                        <>
                                            <Skeleton width = {40} height = {20} />
                                            <Typography >در حال جستجو...</Typography >
                                        </>
                                    )}
                                </Stack >

                                <Box >
                                    <Button
                                        onClick = {handleClose}
                                        sx = {{h: 40}}
                                        variant = "contained"
                                        color = "secondary"
                                    >
                                        مشاهده
                                    </Button >
                                </Box >
                            </Box >
                        </SelectedItemsFilters >
                    )
                }
            >
                <Accordion
                    expanded = {expandedAccordionSort}
                    onChange = {handleChangeSortAccordion}
                    elevation = {0}
                >
                    <AccordionSummary
                        expandIcon = {<FaIcon faIcon = "fa-angle-down" />}
                        sx = {{pb: 1}}
                    >
                        <Typography
                            fontSize = {16}
                            fontWeight = {500}
                        >
                            مرتب‌سازی بر اساس
                        </Typography >
                    </AccordionSummary >
                    <AccordionDetails sx = {{p: 0, pt: 0}} >
                        <List sx = {{px: 0}} >
                            {renderSortOption("fa-arrow-down-a-z", "حروف الفبا", ProductSortOrder.Alphabet)}
                            <Divider sx = {{mx: 3}} />
                            {renderSortOption("fa-truck-clock", "جدیدترین", ProductSortOrder.Newest)}
                            <Divider sx = {{mx: 3}} />
                            {renderSortOption("fa-arrow-down-1-9", "کمترین قیمت", ProductSortOrder.Cheapest)}
                            <Divider sx = {{mx: 3}} />
                            {renderSortOption("fa-arrow-up-9-1", "بیشترین قیمت", ProductSortOrder.MostExpensive)}
                        </List >
                    </AccordionDetails >
                </Accordion >

                <Accordion
                    expanded = {expandedAccordion}
                    onChange = {handleChangeAccordion}
                    elevation = {0}
                    sx = {{
                        width: "100%",
                        border: "none !important",
                        position: "static",
                        mt: "0 !important",
                    }}
                >
                    <AccordionSummary expandIcon = {<FaIcon faIcon = "fa-angle-down" />} >
                        <Typography fontSize = {16} fontWeight = {500} >
                            فیلتر قطعات
                        </Typography >
                    </AccordionSummary >
                    <AccordionDetails sx = {{py: 0, pl: 1, pr: 0, my: 1}} >
                        <ProductFilters />
                    </AccordionDetails >
                </Accordion >
                <Box pb = {12} />
            </LayoutDialog >
        </>
    );
}
